import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { __ } from '@wordpress/i18n';
import Field from '../Field/Field';
import Group from '../Group/Group';
import { organizeFieldsByGroup } from '../utils/fieldTypes';
import './Tab.css';

const Tab = ({ 
  id, 
  tab, 
  isDragOver,
  dragDirection,
  onEdit, 
  onDelete, 
  onToggleCollapse,
  onAddField, 
  onEditField, 
  onDeleteField, 
  onDeleteGroup,
  onToggleField,
  onToggleFieldRequired,
  onToggleFieldAdminOnly,
  onToggleGroupCollapse,
  onToggleSidebar, // Add this prop
  onMoveFieldUp, 
  onMoveFieldDown,
  onMoveGroupUp,
  onMoveGroupDown,
  showToast,
  isRequiredTab,
  groupCollapsedStates
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 100 : 1,
    boxShadow: isDragging ? 'var(--hvnly-card-shadow)' : 'none',
  };
  
  // Organize fields by group
  const organizedFields = organizeFieldsByGroup(tab.fields || []);
  const { groups, standalone } = organizedFields;
  
  // Create sortable items array for DnD context
  const getSortableItems = () => {
    const items = [];
    
    // Add groups first with their group ID as the sortable ID
    Object.values(groups).forEach(group => {
      items.push({
        id: group.id,
        type: 'group',
        order: group.fields[0]?.order || 0
      });
    });
    
    // Add standalone fields
    standalone.forEach(field => {
      items.push({
        id: field.id,
        type: 'field',
        order: field.order || 0
      });
    });
    
    // Sort by order
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  };
  
  const sortableItems = getSortableItems();
  const sortedItemIds = sortableItems.map(item => item.id);
  
  // Sort groups and standalone for display
  const allGroups = Object.values(groups);
  const sortedGroups = [...allGroups].sort((a, b) => {
    const aOrder = a.fields[0]?.order || 0;
    const bOrder = b.fields[0]?.order || 0;
    return aOrder - bOrder;
  });
  
  const sortedStandalone = [...standalone].sort((a, b) => 
    (a.order || 0) - (b.order || 0)
  );
  
  // Combine for display order
  const displayItems = [];
  let groupIndex = 0;
  let fieldIndex = 0;
  
  sortableItems.forEach(item => {
    if (item.type === 'group') {
      displayItems.push({
        type: 'group',
        data: sortedGroups[groupIndex],
        displayIndex: groupIndex
      });
      groupIndex++;
    } else {
      displayItems.push({
        type: 'field',
        data: sortedStandalone[fieldIndex],
        displayIndex: fieldIndex
      });
      fieldIndex++;
    }
  });
  
  const handleDelete = (e) => {
    e.stopPropagation();
    if (isRequiredTab(tab)) {
      showToast('error', __('Cannot Delete', 'havenlytics'), __('This tab is required and cannot be deleted', 'havenlytics'));
      return;
    }
    
    onDelete();
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    if (isRequiredTab(tab)) {
      showToast('error', __('Cannot Edit', 'havenlytics'), __('This tab is required and cannot be edited', 'havenlytics'));
      return;
    }
    onEdit();
  };
  
  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`hvnly--builder--int_api__property_builder_tab ${isDragOver ? 'hvnly--builder--int_api__drag_over_tab' : ''} ${isRequiredTab(tab) ? 'hvnly--builder--int_api__required_tab' : ''} ${isDragging ? 'dragging' : ''}`}
      data-tab-id={tab.id}
    >
      <div className="hvnly--builder--int_api__property_builder_tab_header">
        <div 
          className="hvnly--builder--int_api__drag_handle"
          {...attributes}
          {...listeners}
          style={{ cursor: isRequiredTab(tab) ? 'not-allowed' : 'grab' }}
          title={isRequiredTab(tab) ? __('Required tab cannot be moved', 'havenlytics') : __('Drag to reorder', 'havenlytics')}
        >
          <i className={`fas fa-${isRequiredTab(tab) ? 'lock' : 'list'}`}></i>
        </div>
        
        <button
          className="hvnly--builder--int_api__tab_collapse_btn"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse();
          }}
          title={tab.collapsed ? __('Expand', 'havenlytics') : __('Collapse', 'havenlytics')}
        >
          <i className={`fas fa-chevron-${tab.collapsed ? 'down' : 'up'}`}></i>
        </button>
        
        <h4>
          <i className={`fas fa-${tab.icon}`}></i>
          {tab.title}
          {isRequiredTab(tab) && (
            <span className="hvnly--builder--int_api__required_badge">
              <i className="fas fa-lock"></i> {__('Required', 'havenlytics')}
            </span>
          )}
        </h4>
        
        <div className="hvnly--builder--int_api__property_builder_tab_actions">
          {!isRequiredTab(tab) ? (
            <>
              <button 
                className="hvnly--builder--int_api__property_builder_tab_action_btn" 
                onClick={handleEdit}
                title={__("Edit Tab", "havenlytics")}
              >
                <i className="fas fa-edit"></i>
              </button>
              <button 
                className="hvnly--builder--int_api__property_builder_tab_action_btn" 
                onClick={handleDelete}
                title={__("Delete Tab", "havenlytics")}
              >
                <i className="fas fa-trash"></i>
              </button>
            </>
          ) : (
            <button 
              className="hvnly--builder--int_api__property_builder_tab_action_btn hvnly--builder--int_api__locked_action_btn" 
              title={__("Required Tab - Cannot be modified", "havenlytics")}
              disabled
            >
              <i className="fas fa-lock"></i>
            </button>
          )}
        </div>
      </div>
      
      {!tab.collapsed && (
        <div className="hvnly--builder--int_api__property_builder_tab_content">
          {displayItems.length > 0 ? (
            <SortableContext 
              items={sortedItemIds}
              strategy={verticalListSortingStrategy}
            >
              {displayItems.map((item, index) => {
                if (item.type === 'group') {
                  return (
                    <Group
                      key={item.data.id}
                      group={{
                        ...item.data,
                        collapsed: groupCollapsedStates?.[item.data.id] || false
                      }}
                      onDelete={() => onDeleteGroup(item.data.id)}
                      onToggleCollapse={() => onToggleGroupCollapse(item.data.id)}
                      onToggleSidebar={onToggleSidebar} // Pass the function to Group
                      onEditField={(fieldId) => {
                        onEditField(fieldId);
                      }}
                      onDeleteField={(fieldId) => onDeleteField(fieldId)}
                      onToggleField={(fieldId) => onToggleField(fieldId)}
                      onToggleFieldRequired={(fieldId) => onToggleFieldRequired(fieldId)}
                      onToggleFieldAdminOnly={(fieldId) => onToggleFieldAdminOnly(fieldId)}
                      onMoveFieldUp={(fieldId) => onMoveFieldUp(fieldId)}
                      onMoveFieldDown={(fieldId) => onMoveFieldDown(fieldId)}
                      onMoveGroupUp={() => onMoveGroupUp(item.data.id, index)}
                      onMoveGroupDown={() => onMoveGroupDown(item.data.id, index)}
                      showToast={showToast}
                      canMoveUp={index > 0}
                      canMoveDown={index < displayItems.length - 1}
                      groupCollapsedStates={groupCollapsedStates}
                    />
                  );
                } else {
                  return (
                    <Field
                      key={`${item.data.id}-${item.data.order || index}`}
                      id={item.data.id}
                      field={item.data}
                      index={item.data.order || index}
                      fieldsLength={displayItems.length}
                      onEdit={() => {
                        onEditField(item.data.id);
                      }}
                      onDelete={() => onDeleteField(item.data.id)}
                      onToggle={() => {
                        onToggleField(item.data.id);
                      }}
                      onToggleRequired={() => {
                        onToggleFieldRequired(item.data.id);
                      }}
                      onToggleAdminOnly={() => {
                        onToggleFieldAdminOnly(item.data.id);
                      }}
                      onMoveUp={() => {
                        onMoveFieldUp(item.data.id);
                      }}
                      onMoveDown={() => {
                        onMoveFieldDown(item.data.id);
                      }}
                      showToast={showToast}
                      isFieldLocked={item.data.locked}
                    />
                  );
                }
              })}
            </SortableContext>
          ) : (
            <div className="hvnly--builder--int_api__field_drop_placeholder">
              <i className="fas fa-arrow-down"></i>
              <span>{__('Drop fields here', 'havenlytics')}</span>
            </div>
          )}
          
          {/* Hide "Add New Field" button for required tabs */}
          {!isRequiredTab(tab) && (
            <button 
              className="hvnly--builder--int_api__property_builder_empty_btn" 
              onClick={(e) => {
                e.stopPropagation();
                onAddField();
              }}
            >
              <i className="fas fa-plus"></i> {__('Add New Field', 'havenlytics')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Tab;